/**
 * demo-sound-engine.ts — a tiny, self-contained Web-Audio synthesizer for the demo
 * Soundboard. Plays a real ~20-second sample of each SOUND_LIBRARY entry (binaural
 * beat, isochronic pulse, pure tone, or noise) so a prospect can actually HEAR what
 * the soundboard does. No backend, no audio files, no vendored coupling — pure
 * oscillators + noise buffers, fading in/out and auto-stopping at the sample length.
 *
 * This is demo infrastructure (NOT a synced leaf), so it's free to differ from the
 * real SoundboardAudioEngine — it just has to make a faithful, audible sample.
 */
import type { SoundEntry } from "@/components/soundboard/types";

/** Each sample plays for this long (within the "15s–1min" range the demo advertises). */
export const SAMPLE_SECONDS = 20;

export class DemoSoundEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  // Bilateral output stage (persists across samples): master gain → panner → out,
  // with panLfo sweeping panner.pan between L and R at panHz.
  private panner: StereoPannerNode | null = null;
  private panLfo: OscillatorNode | null = null;
  private nodes: AudioNode[] = [];
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  private volume = 0.3;
  private panHz = 0.6; // L→R→L cycles per second (same semantics as the ball tempo)

  /** Lazily create the AudioContext — must run inside a user gesture (autoplay policy). */
  private ensure(): AudioContext | null {
    if (typeof window === "undefined") return null;
    // Self-heal after destroy() (incl. React StrictMode's dev mount/unmount/mount,
    // which would otherwise leave a CLOSED context that throws on createOscillator).
    if (this.ctx && this.ctx.state === "closed") {
      this.ctx = null;
      this.master = null;
      this.panner = null;
      this.panLfo = null;
    }
    if (!this.ctx) {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.volume;
      // Master → bilateral panner → out. A sine LFO sweeps pan L↔R at panHz, so
      // EVERY sample becomes a bilateral stimulus (the soundboard's point in a BLS app).
      this.panner = this.ctx.createStereoPanner();
      this.master.connect(this.panner).connect(this.ctx.destination);
      this.panLfo = this.ctx.createOscillator();
      this.panLfo.type = "sine";
      this.panLfo.frequency.value = this.panHz;
      this.panLfo.connect(this.panner.pan); // oscillator output is ±1 → full L↔R sweep
      this.panLfo.start();
    }
    if (this.ctx.state === "suspended") this.ctx.resume().catch(() => {});
    return this.ctx;
  }

  isSupported(): boolean {
    return typeof window !== "undefined" && !!(window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
  }

  setVolume(v: number) {
    this.volume = v;
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(v, this.ctx.currentTime, 0.02);
  }

  /** Set the bilateral sweep rate (L→R→L cycles per second). Applies live while playing. */
  setPanHz(hz: number) {
    this.panHz = hz;
    if (this.panLfo && this.ctx) this.panLfo.frequency.setTargetAtTime(hz, this.ctx.currentTime, 0.05);
  }

  /**
   * Play a ~SAMPLE_SECONDS sample of `sound`. Stops anything already playing first.
   * Returns false if Web Audio is unavailable. Calls `onEnd` when the sample finishes.
   */
  play(sound: SoundEntry, onEnd: () => void): boolean {
    this.stop();
    const ctx = this.ensure();
    if (!ctx || !this.master) return false;

    const now = ctx.currentTime;
    const end = now + SAMPLE_SECONDS;
    const fade = 0.4; // gentle fade in/out so it never clicks
    const carrier = sound.carrierHz ?? 220;
    const beat = sound.beatHz ?? 0;

    const envelope = (peak: number): GainNode => {
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(peak, now + fade);
      g.gain.setValueAtTime(peak, Math.max(now + fade, end - fade));
      g.gain.linearRampToValueAtTime(0, end);
      return g;
    };

    if (sound.synthMethod === "binaural") {
      // Two detuned sines summed → an audible beat at the difference frequency, then
      // swept L↔R by the master panner = a pulsing bilateral tone. (Routing both to
      // master instead of static L/R hard-panning is what makes it bilateral.)
      for (const freq of [carrier, carrier + beat]) {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = freq;
        const g = envelope(0.3);
        osc.connect(g).connect(this.master);
        osc.start(now);
        osc.stop(end);
        this.nodes.push(osc);
      }
    } else if (sound.synthMethod === "isochronic") {
      // A carrier gated on/off at the beat rate via a square-wave LFO on the gain.
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = carrier;
      const gate = ctx.createGain();
      gate.gain.value = 0.25;
      const lfo = ctx.createOscillator();
      lfo.type = "square";
      lfo.frequency.value = beat || 6;
      const lfoDepth = ctx.createGain();
      lfoDepth.gain.value = 0.25;
      lfo.connect(lfoDepth).connect(gate.gain);
      const env = envelope(1);
      osc.connect(gate).connect(env).connect(this.master);
      osc.start(now);
      osc.stop(end);
      lfo.start(now);
      lfo.stop(end);
      this.nodes.push(osc, lfo);
    } else if (sound.synthMethod === "pink_noise" || sound.synthMethod === "white_noise") {
      const src = ctx.createBufferSource();
      src.buffer = this.makeNoise(ctx, sound.synthMethod === "pink_noise");
      src.loop = true;
      const g = envelope(0.4);
      src.connect(g).connect(this.master);
      src.start(now);
      src.stop(end);
      this.nodes.push(src);
    } else {
      // pure_tone (Solfeggio / Tuning) — a single centered sine at the carrier.
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = carrier;
      const g = envelope(0.5);
      osc.connect(g).connect(this.master);
      osc.start(now);
      osc.stop(end);
      this.nodes.push(osc);
    }

    this.stopTimer = setTimeout(() => {
      this.cleanup();
      onEnd();
    }, SAMPLE_SECONDS * 1000 + 60);
    return true;
  }

  stop() {
    if (this.stopTimer) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
    this.cleanup();
  }

  private cleanup() {
    for (const n of this.nodes) {
      try {
        (n as OscillatorNode).stop?.();
      } catch {
        /* already stopped */
      }
      try {
        n.disconnect();
      } catch {
        /* already disconnected */
      }
    }
    this.nodes = [];
  }

  /** 2 seconds of looped white or pink noise (Voss-McCartney-ish pink approximation). */
  private makeNoise(ctx: AudioContext, pink: boolean): AudioBuffer {
    const len = ctx.sampleRate * 2;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    let b0 = 0,
      b1 = 0,
      b2 = 0;
    for (let i = 0; i < len; i++) {
      const white = Math.random() * 2 - 1;
      if (pink) {
        b0 = 0.99765 * b0 + white * 0.099046;
        b1 = 0.963 * b1 + white * 0.2965164;
        b2 = 0.57 * b2 + white * 1.0526913;
        data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.2;
      } else {
        data[i] = white * 0.3;
      }
    }
    return buf;
  }

  destroy() {
    this.stop();
    if (this.ctx) {
      this.ctx.close().catch(() => {}); // closing the context also stops the panLfo
      this.ctx = null;
      this.master = null;
      this.panner = null;
      this.panLfo = null;
    }
  }
}
